import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { 
  Users, 
  Calendar, 
  MessageSquare, 
  UserCheck, 
  Clock,
  ChevronRight,
  Trophy,
  Activity
} from 'lucide-react';
import { useChildConnections } from '@/hooks/useChildConnections';
import { useUnifiedUser } from '@/hooks/useUnifiedUser';
import { getUserTypeDisplayName, getUserTypeBadgeVariant } from '@/utils/userTypeDisplay';
import ParentChildConnector from '@/components/dashboard/ParentChildConnector';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import MobileHeader from '@/components/mobile/MobileHeader';
import MobileBottomNav from '@/components/mobile/MobileBottomNav';
import { useIsMobile } from '@/hooks/use-mobile';

const ChildManagement: React.FC = () => {
  const { user: unifiedUser, isLoading: userLoading } = useUnifiedUser();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const {
    connectedChildren,
    childTeams,
    loading,
    error,
    refreshConnections,
    hasConnections,
    totalChildren,
    totalChildTeams
  } = useChildConnections(unifiedUser?.id || '', unifiedUser?.role || '');

  // Debug logging for user ID
  React.useEffect(() => {
    console.log('🔍 CHILD MANAGEMENT - Unified user details:', {
      id: unifiedUser?.id,
      role: unifiedUser?.role,
      userType: unifiedUser?.userType,
      username: unifiedUser?.username
    });
  }, [unifiedUser]);

  // Redirect if not a parent
  if (!userLoading && unifiedUser?.role !== 'parent') {
    navigate('/dashboard');
    return null;
  }

  if (userLoading || loading) {
    return (
      <div className="min-h-screen bg-background">
        {isMobile ? <MobileHeader /> : <Header />}
        <div className="container max-w-4xl mx-auto px-4 py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 bg-muted rounded w-1/3"></div>
            <div className="h-64 bg-muted rounded"></div>
          </div>
        </div>
        {isMobile ? <MobileBottomNav /> : <Footer />}
      </div>
    );
  }

  const handleViewTeams = (childId: string) => {
    // Navigate to teams view filtered by child
    navigate(`/child-teams?child=${childId}`);
  };

  const handleViewChats = (childId: string) => {
    // Navigate to chat with child's groups
    navigate(`/chat?child=${childId}`);
  };

  const getChildTeams = (childId: string) => {
    return childTeams.filter(team => team.child_id === childId);
  };

  return (
    <div className="min-h-screen bg-background">
      {isMobile ? <MobileHeader /> : <Header />}
      <div className="container max-w-4xl mx-auto px-4 py-8" style={{ paddingTop: '30px' }}>
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold tracking-tight">Child Management</h1>
              <p className="text-muted-foreground mt-2">
                Connect and manage your children's sports activities
              </p>
            </div>
            {hasConnections && (
              <Button onClick={refreshConnections} variant="outline">
                <Activity className="h-4 w-4 mr-2" />
                Refresh
              </Button>
            )}
          </div>

          {/* Stats Overview */}
          {hasConnections && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <Users className="h-5 w-5 text-primary" />
                    <div>
                      <p className="text-2xl font-bold">{totalChildren}</p>
                      <p className="text-sm text-muted-foreground">Connected Children</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <Trophy className="h-5 w-5 text-accent" />
                    <div>
                      <p className="text-2xl font-bold">{totalChildTeams}</p>
                      <p className="text-sm text-muted-foreground">Total Teams</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center space-x-2">
                    <UserCheck className="h-5 w-5 text-success" />
                    <div>
                      <p className="text-2xl font-bold">Active</p>
                      <p className="text-sm text-muted-foreground">Connection Status</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Connection Form */}
          <ParentChildConnector 
            parentId={unifiedUser?.id || ''} 
            userRole={unifiedUser?.role || ''}
            onConnectionSuccess={refreshConnections}
          />

          {/* Error State */}
          {error && (
            <Card className="border-destructive/50">
              <CardContent className="p-6">
                <div className="flex items-center space-x-2 text-destructive">
                  <div className="h-2 w-2 rounded-full bg-destructive"></div>
                  <p className="font-medium">Error loading child connections</p>
                </div>
                <p className="text-sm text-muted-foreground mt-1">{error}</p>
                <Button 
                  onClick={refreshConnections} 
                  variant="outline" 
                  size="sm" 
                  className="mt-3"
                >
                  Try Again
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Connected Children Details */}
          {hasConnections && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="h-5 w-5" />
                  Your Children's Activities
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {connectedChildren.map((connection) => {
                  const childTeams = getChildTeams(connection.child_id);
                  
                  return (
                    <div key={connection.id} className="border rounded-lg p-6 space-y-4">
                      {/* Child Header */}
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-4">
                          <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center">
                            <Users className="h-6 w-6 text-primary" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <h3 className="font-semibold text-lg">{connection.child?.username}</h3>
                              <Badge variant={getUserTypeBadgeVariant('internal')}>
                                {getUserTypeDisplayName('internal')}
                              </Badge>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-muted-foreground">
                              <Clock className="h-4 w-4" />
                              Connected on {new Date(connection.connected_at).toLocaleDateString()}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Age Group: {connection.child?.age_group}
                            </p>
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewTeams(connection.child_id)}
                          >
                            <Calendar className="h-4 w-4 mr-1" />
                            Teams ({childTeams.length})
                          </Button>
                          <Button 
                            size="sm" 
                            variant="outline"
                            onClick={() => handleViewChats(connection.child_id)}
                          >
                            <MessageSquare className="h-4 w-4 mr-1" />
                            Chats
                          </Button>
                        </div>
                      </div>

                      {/* Teams Summary */}
                      {childTeams.length > 0 && (
                        <div className="space-y-3">
                          <h4 className="font-medium text-sm text-muted-foreground uppercase tracking-wide">
                            Current Teams
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            {childTeams.map((team) => (
                              <div 
                                key={team.team_id} 
                                className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="h-8 w-8 rounded bg-accent/20 flex items-center justify-center">
                                    <Trophy className="h-4 w-4 text-accent" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-sm">{team.team_name}</p>
                                    <p className="text-xs text-muted-foreground">
                                      {team.age_group} • {team.slug}
                                    </p>
                                  </div>
                                </div>
                                <Button 
                                  size="sm" 
                                  variant="ghost"
                                  onClick={() => navigate(`/team/${team.slug}`)}
                                >
                                  <ChevronRight className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {childTeams.length === 0 && (
                        <div className="text-center py-6 text-muted-foreground">
                          <Trophy className="h-8 w-8 mx-auto mb-2 opacity-50" />
                          <p className="text-sm">No teams found for this child</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>
          )}

          {/* Empty State */}
          {!loading && !hasConnections && !error && (
            <Card className="text-center py-12">
              <CardContent>
                <Users className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
                <h3 className="font-semibold mb-2">No Connected Children</h3>
                <p className="text-muted-foreground mb-6">
                  Use the form above to connect to your child's account using their connection code.
                </p>
              </CardContent>
            </Card>
          )}
        </div>
      </div>
      {isMobile ? <MobileBottomNav /> : <Footer />}
    </div>
  );
};

export default ChildManagement;